import { useState, useEffect } from 'react';
import {
  Award,
  Calendar,
  Trophy,
  TrendingUp,
  Clock,
  Target,
  History
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import BeltBadge, { getBeltName, getBeltBadgeColors } from '../shared/BeltBadge';

import StudentHistoryForm from './StudentHistoryForm';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json'
});

// ============================================
// Progress Stats - Modern Dojo Style
// ============================================
const ProgressStats = ({ stats }) => {
  const statItems = [
    {
      label: 'Classes This Month',
      value: stats.classesThisMonth || 0,
      icon: Calendar,
      color: 'text-[#9B1B30]',
      bgColor: 'bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20'
    },
    {
      label: 'Total Classes',
      value: stats.totalClasses || 0,
      icon: Clock,
      color: 'text-[#C9A227]',
      bgColor: 'bg-[#C9A227]/10 dark:bg-[#C9A227]/20'
    },
    {
      label: 'Promotions',
      value: stats.promotions || 0,
      icon: Award,
      color: 'text-[#9B1B30]',
      bgColor: 'bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20'
    },
    {
      label: 'Tournaments',
      value: stats.tournaments || 0,
      icon: Trophy,
      color: 'text-[#C9A227]',
      bgColor: 'bg-[#C9A227]/10 dark:bg-[#C9A227]/20'
    }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {statItems.map((item, idx) => (
        <div
          key={idx}
          className="member-stat-card flex flex-col items-center group hover:border-[#C9A227] transition-all duration-300"
        >
          <div className={`p-2.5 rounded-full ${item.bgColor} mb-2 group-hover:scale-110 transition-transform duration-300`}>
            <item.icon className={`h-5 w-5 ${item.color}`} />
          </div>
          <p className="text-3xl font-jetbrains font-bold text-stone-800 dark:text-stone-100">{item.value}</p>
          <p className="text-xs text-stone-500 dark:text-stone-400 text-center font-dm-sans">{item.label}</p>
        </div>
      ))}
    </div>
  );
};

// ============================================
// Belt Timeline
// ============================================
const BeltTimeline = ({ promotions }) => {
  if (promotions.length === 0) {
    return (
      <div className="text-center py-4 text-stone-500 dark:text-stone-400 text-sm">
        No promotions recorded yet. Keep training!
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {promotions.slice(0, 5).map((promotion, idx) => {
        const beltColors = getBeltBadgeColors(promotion.to_belt);
        return (
          <div
            key={promotion.id}
            className="flex items-center gap-3 p-3 rounded-lg border"
            style={{ backgroundColor: beltColors.bg, borderColor: beltColors.border }}
          >
            <div className="flex items-center gap-2">
              <BeltBadge belt={promotion.from_belt} stripes={promotion.from_stripes} size="sm" />
              <span style={{ color: beltColors.text }}>→</span>
              <BeltBadge belt={promotion.to_belt} stripes={promotion.to_stripes} size="sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium" style={{ color: beltColors.text }}>
                {getBeltName(promotion.to_belt, promotion.to_stripes)}
              </p>
              <p className="text-xs opacity-75" style={{ color: beltColors.text }}>
                {new Date(promotion.promotion_date).toLocaleDateString()}
              </p>
            </div>
            {idx === 0 && (
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 text-xs rounded-full">
                Latest
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// Main Progress Card
// ============================================
const StudentProgressCard = ({ student, showJourneyOnly = false }) => {
  const [progress, setProgress] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistoryForm, setShowHistoryForm] = useState(false);

  useEffect(() => {
    if (student?.id) {
      fetchProgress();
      fetchTournaments();
    }
  }, [student?.id]);

  const fetchProgress = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/progress`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/tournaments`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setTournaments(data);
      }
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    }
  };

  if (!student) {
    return null;
  }

  if (loading) {
    return (
      <Card className="member-card">
        <CardContent className="py-8 text-center text-stone-500 dark:text-stone-400">
          Loading progress...
        </CardContent>
      </Card>
    );
  }

  // Calculate training duration
  const startDate = student.start_date ? new Date(student.start_date) : null;
  const monthsTraining = startDate
    ? Math.floor((new Date() - startDate) / (1000 * 60 * 60 * 24 * 30))
    : 0;

  // Journey-only view (Belt Timeline + Tournaments)
  if (showJourneyOnly) {
    return (
      <div className="space-y-4">
        {/* Belt Journey */}
        <Card className="member-card">
          <CardHeader className="pb-2 border-b border-stone-200 dark:border-stone-700 bg-gradient-to-r from-transparent to-stone-50 dark:to-stone-800/50">
            <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-100 font-outfit">
              <Award className="h-5 w-5 text-[#C9A227]" />
              Belt Journey
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <BeltTimeline promotions={progress?.promotions || []} />
          </CardContent>
        </Card>

        {/* Tournament Highlights (if any) */}
        {tournaments && tournaments.length > 0 && (
          <Card className="member-card">
            <CardHeader className="pb-2 border-b border-stone-200 dark:border-stone-700 bg-gradient-to-r from-transparent to-stone-50 dark:to-stone-800/50">
              <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-100 font-outfit">
                <Trophy className="h-5 w-5 text-[#C9A227]" />
                Tournament History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {tournaments.map((tournament) => {
                  const getMedalStyle = (place) => {
                    switch (place) {
                      case '1': return { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-300', label: '1st Place (Gold)' };
                      case '2': return { bg: 'bg-stone-200 dark:bg-stone-600', text: 'text-stone-700 dark:text-stone-200', label: '2nd Place (Silver)' };
                      case '3': return { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-700 dark:text-amber-300', label: '3rd Place (Bronze)' };
                      default: return { bg: 'bg-blue-50 dark:bg-blue-900/50', text: 'text-blue-600 dark:text-blue-300', label: 'Participated' };
                    }
                  };
                  const formatEventType = (type) => {
                    const types = { kata: 'Kata', kumite: 'Kumite', kobudo: 'Kobudo', team_kata: 'Team Kata', other: 'Other' };
                    return types[type] || type;
                  };
                  return (
                    <div key={tournament.id} className="member-stat-card">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-stone-800 dark:text-stone-100">{tournament.name}</p>
                          <p className="text-sm text-stone-500 dark:text-stone-400">
                            {new Date(tournament.date).toLocaleDateString()}
                            {tournament.location && ` • ${tournament.location}`}
                          </p>
                        </div>
                      </div>
                      {tournament.results && tournament.results.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {tournament.results.map((result, idx) => {
                            const style = getMedalStyle(result.place);
                            return (
                              <div key={idx} className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${style.bg}`}>
                                {['1', '2', '3'].includes(result.place) && <Trophy className={`h-3 w-3 ${style.text}`} />}
                                <span className={`font-medium ${style.text}`}>{formatEventType(result.event_type)}</span>
                                <span className={style.text}>•</span>
                                <span className={style.text}>
                                  {result.place === '1' ? '1st' : result.place === '2' ? '2nd' : result.place === '3' ? '3rd' : 'Participated'}
                                </span>
                                {result.division && <span className="text-stone-500 dark:text-stone-400">({result.division})</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No tournaments message */}
        {(!tournaments || tournaments.length === 0) && (
          <Card className="member-card">
            <CardContent className="py-8 text-center text-stone-500 dark:text-stone-400">
              No tournament history yet. Compete to see your results here!
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Overview view - just Current Belt and Stats (Belt Journey and Tournaments are in Journey tab)
  return (
    <div className="space-y-4">
      {/* Current Belt & Training Info */}
      <Card className="member-card member-card-highlight dojo-animate border-l-4 border-l-[#9B1B30]">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <BeltBadge
                belt={student.current_belt}
                stripes={student.current_stripes}
                size="lg"
              />
              <div>
                <h3 className="text-xl font-outfit font-bold text-stone-800 dark:text-stone-100">
                  {getBeltName(student.current_belt, student.current_stripes)}
                </h3>
                <p className="text-sm text-stone-500 dark:text-stone-400 font-dm-sans">Current Rank</p>
              </div>
            </div>

            {startDate && (
              <div className="member-stat-card flex items-center gap-3 hover:border-[#C9A227] transition-all duration-300">
                <div className="p-2 rounded-full bg-[#C9A227]/10">
                  <TrendingUp className="h-5 w-5 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-lg font-jetbrains font-bold text-stone-800 dark:text-stone-100">
                    {monthsTraining} <span className="text-sm font-dm-sans font-medium">{monthsTraining === 1 ? 'month' : 'months'}</span>
                  </p>
                  <p className="text-xs text-stone-500 dark:text-stone-400 font-dm-sans">Training Since {startDate.toLocaleDateString()}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <Card className="member-card dojo-animate dojo-delay-1">
        <CardHeader className="pb-2 border-b border-stone-200 dark:border-stone-700 bg-gradient-to-r from-transparent to-stone-50 dark:to-stone-800/50">
          <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-100 font-outfit">
            <Target className="h-5 w-5 text-[#9B1B30]" />
            Progress Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <ProgressStats
            stats={{
              classesThisMonth: progress?.stats?.classes_this_month || 0,
              totalClasses: progress?.stats?.total_classes || 0,
              promotions: progress?.promotions?.length || 0,
              tournaments: tournaments?.length || progress?.stats?.tournament_count || 0,
            }}
          />
        </CardContent>
      </Card>

      {/* Share History Button */}
      <div className="flex justify-center">
        <Button
          variant="outline"
          onClick={() => setShowHistoryForm(true)}
          className="text-sm text-stone-600 dark:text-stone-400 border-stone-300 dark:border-stone-600 hover:border-[#C9A227] hover:text-[#C9A227]"
        >
          <History className="h-4 w-4 mr-2" />
          Share Your History
        </Button>
      </div>

      <StudentHistoryForm
        studentId={student.id}
        open={showHistoryForm}
        onOpenChange={setShowHistoryForm}
      />
    </div>
  );
};

export default StudentProgressCard;
export { ProgressStats, BeltTimeline };
